import React from 'react';
import { basePaths } from 'app-constants';
import { TrackedLink } from 'components/common/TrackedLink';
import { useWorkspacePath } from 'pages/workspaces/useWorkspace';
import { useTabbableClick } from './useTabbableClick';
import { useQueryAndHash } from './useQueryAndHash';
import { LinkHelperProps } from './index';

type StatuteType = 'code' | 'constitution' | 'constitution-node';

interface BaseStatuteLinkProps extends LinkHelperProps {
  type?: StatuteType;
  citator?: boolean;
}

interface CAStatuteLinkProps extends BaseStatuteLinkProps {
  type?: 'code';
  jurisdictionId: string;
  codeJudicataId?: string;
  sectionBegin?: string;
  sectionEnd?: string;
  citator?: boolean;
}

interface CAConstitutionLinkProps extends BaseStatuteLinkProps {
  type?: 'constitution';
  article?: string;
  section?: string;
  citator?: boolean;
}

export type StatuteLinkProps = CAStatuteLinkProps | CAConstitutionLinkProps;

export function parseStatutePath(path: string) {
  // parse a string into a StatuteLinkProps object
  // This function parses a statute path string into a StatuteLinkProps object.
  // Handles both code and constitution paths.
  // Example code path: "/primary-law/statutes/ca/codes/ABC/123/456/citator"
  // Example constitution path: "/primary-law/statutes/ca/const/I/1/citator"
  const parts = path.replace(/^\//, '').split('/');

  // Remove workspace prefix if present
  let i = 0;
  if (parts[i] === 'workspaces') {
    // skip workspace slug as well
    i += 2;
  }

  // Handle base path that may be multiple segments (e.g. /primary-law/statutes)
  const basePathParts = basePaths.statutes.replace(/^\//, '').split('/');
  let basePathMatch = true;
  for (let k = 0; k < basePathParts.length; k++) {
    if (parts[i + k] !== basePathParts[k]) {
      basePathMatch = false;
      break;
    }
  }
  if (!basePathMatch) {
    return null;
  }
  i += basePathParts.length;

  // Constitution: /primary-law/statutes/ca/const/:article/:section/[:citator]
  if (parts[i] === 'ca' && parts[i + 1] === 'const') {
    const article = parts[i + 2];
    const section = parts[i + 3];
    const citator = parts[i + 4] === 'citator';
    return {
      type: 'constitution',
      article,
      section,
      citator,
    } as CAConstitutionLinkProps;
  }

  // Code: /primary-law/statutes/:jurisdictionId/codes/:codeJudicataId/[:sectionBegin]/[:sectionEnd]/[citator]
  const jurisdictionId = parts[i];
  if (parts[i + 1] === 'codes') {
    const codeJudicataId = parts[i + 2];
    let sectionBegin: string | undefined;
    let sectionEnd: string | undefined;
    let citator = false;
    let j = i + 3;

    if (parts[j] && parts[j] !== 'citator') {
      sectionBegin = parts[j];
      j++;
    }
    if (parts[j] && parts[j] !== 'citator') {
      sectionEnd = parts[j];
      j++;
    }
    if (parts[j] === 'citator') {
      citator = true;
    }

    return {
      type: 'code',
      jurisdictionId,
      codeJudicataId,
      sectionBegin,
      sectionEnd,
      citator,
    } as CAStatuteLinkProps;
  }
  return null;
}

export function useGetStatutePath() {
  const workspacePath = useWorkspacePath();

  return React.useCallback(
    (props: StatuteLinkProps) => {
      const parts: (string | undefined)[] = [basePaths.statutes];

      switch (props.type) {
        case 'constitution':
          if ('article' in props && props.article) {
            parts.push(
              'ca',
              'const',
              props.article,
              props.section,
              props.citator ? 'citator' : undefined,
            );
          }
          break;
        default:
          if (
            'jurisdictionId' in props &&
            'codeJudicataId' in props &&
            props.jurisdictionId &&
            props.codeJudicataId
          ) {
            parts.push(props.jurisdictionId, 'codes', props.codeJudicataId);
            if ('sectionBegin' in props && props.sectionBegin)
              parts.push(props.sectionBegin);
            if ('sectionEnd' in props && props.sectionEnd)
              parts.push(props.sectionEnd);
            if (props.citator) parts.push('citator');
          }
      }

      const basePath = parts.filter(Boolean).join('/');
      return workspacePath + basePath;
    },
    [workspacePath],
  );
}

export function useStatutePath(props: StatuteLinkProps) {
  const getPath = useGetStatutePath();
  const path = React.useMemo(() => getPath(props), [getPath, props]);
  return useQueryAndHash(path, props.query, props.hash);
}

export function StatuteLink(props: StatuteLinkProps) {
  const {
    type,
    article,
    section,
    jurisdictionId,
    codeJudicataId,
    sectionBegin,
    sectionEnd,
    onClick,
    tabbable,
    citator,
    query,
    hash,
    ...restProps
  } = props;
  const path = useStatutePath({
    type,
    article,
    section,
    citator,
    jurisdictionId,
    codeJudicataId,
    sectionBegin,
    sectionEnd,
    query,
    hash,
  });
  const handleClick = useTabbableClick(path.pathname, tabbable, onClick);

  return (
    <TrackedLink
      to={props.to ? props.to : path}
      onClick={handleClick}
      {...restProps}
    />
  );
}

// Convenience component for constitution links
export function ConstitutionLink(props: Omit<CAConstitutionLinkProps, 'type'>) {
  return <StatuteLink type="constitution" {...props} />;
}
